import {
  Configuration,
  PersonSessionCreate,
  PersonSessionCreateSessionType,
  SessionCreate,
  SessionsApi
} from "@wingspanhq/wingspan-api-sdk";
import { config } from "../config";
import { tokenStore } from "./tokenStore";

class SessionService {
  private readonly api = new SessionsApi(
    new Configuration({
      basePath: config.apiBaseUrl,
      accessToken: () => tokenStore.getAccessToken() ?? ""
    })
  );

  private refreshInFlight: Promise<void> | null = null;

  async login(
    email: string,
    password: string,
    persist: boolean
  ): Promise<void> {
    const sessionCreate: PersonSessionCreate = {
      sessionType: PersonSessionCreateSessionType.PERSON,
      email,
      password
    };

    const session = await this.api.createSession({
      sessionCreate: sessionCreate as unknown as SessionCreate
    });
    tokenStore.setSession(session, persist);
  }

  async switchAccount(
    accountId: string,
    options?: { preferred?: boolean; reload?: boolean }
  ): Promise<void> {
    const identity = tokenStore.getIdentity();
    if (!identity) {
      throw new Error("Cannot switch account without an active session");
    }

    const session = await this.api.updateSession({
      sessionId: identity.sessionId,
      sessionPatch: { accountScope: { accountId } }
    });
    tokenStore.setSession(session);
    if (options?.preferred && identity.personId) {
      tokenStore.setPreferredAccountId(identity.personId, accountId);
    }

    if (options?.reload) {
      window.location.reload();
    }
  }

  async logout(): Promise<void> {
    const identity = tokenStore.getIdentity();
    try {
      if (identity) {
        await this.api.deleteSession({ sessionId: identity.sessionId });
      }
    } finally {
      tokenStore.clear();
    }
  }

  refreshAccessToken(): Promise<void> {
    if (!this.refreshInFlight) {
      const refreshToken = tokenStore.getRefreshToken();
      if (!refreshToken) {
        return Promise.reject(new Error("No refresh token available"));
      }

      this.refreshInFlight = this.api
        .refreshSession({ refreshRequest: { refreshToken } })
        .then(session => tokenStore.setSession(session))
        .finally(() => {
          this.refreshInFlight = null;
        });
    }

    return this.refreshInFlight;
  }

  async restore(): Promise<boolean> {
    if (!tokenStore.getRefreshToken()) {
      return false;
    }

    try {
      await this.refreshAccessToken();
      return true;
    } catch {
      tokenStore.clear();
      return false;
    }
  }
}

export const sessionService = new SessionService();
